/**
 * Pintar una imagen del club: el logo, la foto de la ficha · §S.3
 *
 * ## Por qué no es un `<img src="/v1/files/…">`
 *
 * Por lo mismo que `descargas.ts` no es un `<a href>`: `GET /files/{id}` pide
 * `Authorization: Bearer` y el token vive en memoria, a propósito. Una etiqueta
 * `<img>` no manda cabeceras, así que recibiría un `401`, y meter el token en la
 * URL es lo que `BFF_BO.md` §3.3 prohíbe.
 *
 * ## Y por qué no se sigue la redirección
 *
 * La ruta redirige al almacén, y un `fetch` con credenciales **no puede seguirla**:
 * salta de la API a un tercer origen y el navegador lo manda con `Origin: null`, que
 * ningún CORS de bucket acepta. Se vio el 24-09-2026: «Failed to fetch». Así que se
 * pide la URL firmada en JSON (`Accept: application/json`) y va tal cual a un
 * `<img src>`, que no necesita CORS.
 *
 * ## Cuatro minutos, porque la URL caduca a los cinco
 *
 * Una imagen ya pintada no vuelve a pedir nada y sigue viéndose aunque su URL
 * caduque. Lo que no puede pasar es que una pantalla que se abre **después** reciba
 * de la caché una URL muerta, así que a los cuatro minutos se pide otra.
 */
import { useQuery } from '@tanstack/react-query';
import { opcionesDeSesion } from '@/shared/api/client';
import { fetchConSesion } from '@/shared/api/sesion';

const VIGENCIA_MS = 4 * 60_000;

/**
 * La URL de una imagen del almacén, o `null` mientras no la hay.
 *
 * `null` también si falla: es decoración, y una pantalla que se cae porque el
 * logo no carga es peor que una sin logo. Quien la usa pinta su alternativa.
 */
export function useImagen(assetId: string | null | undefined): string | null {
  const imagen = useQuery({
    queryKey: ['imagen', assetId],
    enabled: Boolean(assetId),
    staleTime: VIGENCIA_MS,
    gcTime: VIGENCIA_MS,
    retry: 1,
    queryFn: async () => {
      const r = await fetchConSesion(opcionesDeSesion)(
        `${opcionesDeSesion.base}/v1/files/${assetId}`,
        { headers: { accept: 'application/json' } }
      );
      if (!r.ok) throw new Error(`La imagen respondió ${r.status}`);
      return ((await r.json()) as { url: string }).url;
    },
  });

  return imagen.data ?? null;
}
